import React from 'react'
import PropTypes from 'prop-types'
import Markdown from 'react-markdown'
import { useTranslation } from 'react-i18next'

import { useSentinelChat } from './SentinelChatProvider'

// Render markdown inline (no <p>) so option bodies are valid inside a <button>.
const INLINE_MD = { p: ({ children }) => <>{children}</> }

// Plain text for the reply we send when an option is clicked.
const toPlain = (s) => (s || '').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim()

/**
 * Split a clarifying question of the shape
 *   "<intro> - **Option A:** … - **Option B:** … <trailing question?>"
 * into an intro, lettered options, and an optional trailing prompt. Inline
 * markdown (e.g. **Berlin**) is preserved for the renderer. Returns null when
 * the text doesn't look like a multi-option prompt (renders as-is instead).
 */
function parseOptions(raw) {
  try {
    const text = (raw || '')
      .replace(/\*\*\s*(Option\s+[A-Za-z0-9]+)\s*:?\s*\*\*/gi, '$1:') // unwrap bold around the label
      .trim()
    const markers = [...text.matchAll(/Option\s+([A-Za-z0-9]+)\s*:/gi)]
    if (markers.length < 2) return null

    const intro = text.slice(0, markers[0].index).replace(/[\s\-–—:]+$/, '').trim()
    const opts = markers.map((m, i) => {
      const start = m.index + m[0].length
      const end = i + 1 < markers.length ? markers[i + 1].index : text.length
      const body = text
        .slice(start, end)
        .replace(/^[\s\-–—:]+/, '')
        .replace(/[\s\-–—]+$/, '')
        .trim()
      return { label: String(m[1]).toUpperCase(), body }
    })

    // Peel a trailing question sentence off the last option into its own prompt.
    let outro = ''
    const last = opts[opts.length - 1]
    const tail = last.body.match(/^([\s\S]*[.!?])\s+([A-Z][^.?!]*\?)$/)
    if (tail) {
      last.body = tail[1].trim()
      outro = tail[2].trim()
    }
    return { intro, opts, outro }
  } catch {
    return null
  }
}

export default function ClarifyingQuestion({ question }) {
  const { t } = useTranslation()
  const { answerClarification } = useSentinelChat()
  const parsed = parseOptions(question)

  return (
    <div className="fd-card" style={{ borderLeft: '3px solid var(--fd-teal-400)', maxWidth: '640px' }}>
      <div className="fd-eyebrow" style={{ color: 'var(--fd-teal-700)', marginBottom: '6px' }}>
        {t('Sentinel.V2.clarify.title')}
      </div>

      {parsed ? (
        <>
          {parsed.intro && (
            <div className="fd-clarify__text fd-body"><Markdown>{parsed.intro}</Markdown></div>
          )}
          <div className="fd-clarify__opts">
            {parsed.opts.map((o) => (
              <button
                key={o.label}
                type="button"
                className="fd-clarify-opt"
                onClick={() => answerClarification(toPlain(o.body))}
              >
                <span className="fd-clarify-opt__badge">{o.label}</span>
                <span className="fd-clarify-opt__body"><Markdown components={INLINE_MD}>{o.body}</Markdown></span>
                <i className="fa-solid fa-arrow-right fd-clarify-opt__go" aria-hidden="true" />
              </button>
            ))}
          </div>
          {parsed.outro && (
            <div className="fd-clarify__text fd-body" style={{ marginTop: '10px' }}><Markdown>{parsed.outro}</Markdown></div>
          )}
        </>
      ) : (
        <div className="fd-clarify__text fd-body" style={{ marginBottom: '4px' }}><Markdown>{question || ''}</Markdown></div>
      )}

      <div className="flex items-center gap-2 fd-body-sm" style={{ color: 'var(--text-tertiary)', marginTop: '12px' }}>
        <i className="fa-solid fa-arrow-down" aria-hidden="true" />
        {t('Sentinel.V2.clarify.hint')}
      </div>
    </div>
  )
}

ClarifyingQuestion.propTypes = { question: PropTypes.string }
